import React from "react";
import { motion } from "framer-motion";
import { Check, Flame, Plus, Minus, Pencil, Trash2, Clock, AlertTriangle } from "lucide-react";

export default function HabitCard({ habit, onToggle, onSetCount, onEdit, onDelete, disabled }) {
  const isCountable = habit.type === "countable";
  const progress = isCountable ? Math.min(100, Math.round((habit.count / (habit.target || 1)) * 100)) : (habit.completed ? 100 : 0);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`habit-card-${habit.id}`}
      className={`group relative p-5 rounded-2xl border bg-[#12141C] hover:bg-[#181B26] transition-colors shadow-xl ${
        habit.completed ? "border-emerald-500/50" : "border-[#242417]"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${habit.color}22`, border: `1px solid ${habit.color}55` }}>
            <span className="w-3 h-3 rounded-full" style={{ background: habit.color }} />
          </div>
          <div className="min-w-0">
            <h3 className="font-display text-lg font-semibold truncate">{habit.title}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white border border-[#242417]">{habit.category}</span>
              {habit.reminder && (
                <span className="text-xs text-white flex items-center gap-1"><Clock className="w-3 h-3" />{habit.reminder}</span>
              )}
              {habit.missed_yesterday && (
                <span data-testid={`miss-twice-warning-${habit.id}`} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Don't miss twice
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button data-testid={`habit-edit-button-${habit.id}`} onClick={() => onEdit(habit)} className="p-1.5 rounded-lg text-white hover:text-white hover:bg-white/5">
            <Pencil className="w-4 h-4" />
          </button>
          <button data-testid={`habit-delete-button-${habit.id}`} onClick={() => onDelete(habit)} className="p-1.5 rounded-lg text-white hover:text-red-400 hover:bg-red-500/10">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {habit.description && <p className="text-sm text-white mb-4 line-clamp-2">{habit.description}</p>}

      {/* Streak + progress bar */}
      <div className="flex items-center justify-between mb-4">
        <div className={`flex items-center gap-1.5 text-sm font-semibold ${habit.streak > 0 ? "text-orange-400" : "text-white"}`}>
          <Flame className={`w-4 h-4 ${habit.streak > 0 ? "fill-orange-500/30" : ""}`} />
          <span data-testid={`habit-streak-${habit.id}`} className="font-mono-stat">{habit.streak}</span>
          <span className="text-white font-normal">day streak</span>
        </div>
        {isCountable && (
          <span className="text-sm font-mono-stat text-white" data-testid={`habit-count-${habit.id}`}>
            {habit.count}/{habit.target} {habit.unit}
          </span>
        )}
      </div>

      <div className="h-2 rounded-full bg-[#1c1c12] overflow-hidden mb-4">
        <motion.div
          className="h-full rounded-full"
          style={{ background: `linear-gradient(90deg, ${habit.color}, ${habit.color}cc)` }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Action */}
      {isCountable ? (
        <div className="flex items-center gap-3">
          <button
            data-testid={`habit-decrement-button-${habit.id}`}
            disabled={disabled || habit.count <= 0}
            onClick={() => onSetCount(habit, Math.max(0, habit.count - 1))}
            className="w-11 h-11 rounded-xl border border-[#33331f] flex items-center justify-center text-white hover:bg-white/5 disabled:opacity-40 transition-colors"
          >
            <Minus className="w-5 h-5" />
          </button>
          <div className={`flex-1 h-11 rounded-xl flex items-center justify-center font-semibold ${habit.completed ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-white/5 text-white"}`}>
            {habit.completed ? "Goal Complete 🎉" : `${habit.count} logged`}
          </div>
          <button
            data-testid={`habit-increment-button-${habit.id}`}
            disabled={disabled}
            onClick={() => onSetCount(habit, habit.count + 1)}
            className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center text-black hover:opacity-90 disabled:opacity-40 transition-opacity"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <button
          data-testid={`habit-check-button-${habit.id}`}
          disabled={disabled}
          onClick={() => onToggle(habit)}
          className={`w-full h-11 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
            habit.completed
              ? "bg-gradient-to-r from-emerald-500 to-teal-400 text-black glow-emerald"
              : "border border-[#33331f] text-white hover:bg-white/5"
          }`}
        >
          <span className={`w-5 h-5 rounded-md border flex items-center justify-center ${habit.completed ? "bg-black/20 border-black/30" : "border-[#4d4b2a]"}`}>
            {habit.completed && <Check className="w-4 h-4" />}
          </span>
          {habit.completed ? "Completed" : "Mark Done"}
        </button>
      )}
    </motion.div>
  );
}
